/**
 * @fileoverview Staff Contracts page — non-player employment contracts
 * Tracks coaches, medical, scouting, and admin staff salaries with allowances.
 */

import { useState, useMemo, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftBadge from "components/SoftBadge";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import { formatCompact, formatDateIN, CLUB_NAME } from "utils/indiaLocale";
import { PermissionGate } from "components/PermissionGate";
import { financeService } from "api";
import FootballLoader from "components/FootballLoader";
import { useAuth } from "context/AuthContext";

// ── Constants ────────────────────────────────────────────────────────────────
const DEPARTMENTS = ["Coaching", "Medical", "Scouting", "Sports Science", "Administration", "Marketing", "Operations", "Security"];
const CONTRACT_TYPES = ["Permanent", "Consultant", "Seasonal", "Probation"];
const STATUS_COLORS = { Active: "success", Expired: "error", Terminated: "error", OnLeave: "warning" };

const EMPTY_FORM = {
  staffName: "", staffRole: "", department: "", contractType: "Permanent",
  monthlySalaryAmount: "", currency: "INR",
  housingAllowance: "", transportAllowance: "", performanceBonusAnnual: "",
  startDate: "", endDate: "", email: "", phoneNumber: "", notes: "",
};

// ── Mapper ───────────────────────────────────────────────────────────────────
function mapDto(dto) {
  return {
    id: dto.id,
    staffName: dto.staffName,
    staffRole: dto.staffRole,
    department: dto.department,
    contractType: dto.contractType,
    monthlySalary: dto.monthlySalaryAmount,
    currency: dto.currency,
    housingAllowance: dto.housingAllowance,
    transportAllowance: dto.transportAllowance,
    performanceBonus: dto.performanceBonusAnnual,
    totalPackage: dto.totalMonthlyPackage,
    startDate: dto.startDate,
    endDate: dto.endDate,
    status: dto.status,
    daysRemaining: dto.daysRemaining,
    email: dto.email,
    phoneNumber: dto.phoneNumber,
    notes: dto.notes,
  };
}

// ── Component ─────────────────────────────────────────────────────────────────
function StaffContractsPage() {
  const { currentUser } = useAuth();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("Active");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});

  const [terminateId, setTerminateId] = useState(null);

  const fetchContracts = useCallback(() => {
    setLoading(true);
    financeService.getStaffContracts()
      .then((data) => setContracts(data.map(mapDto)))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchContracts(); }, [fetchContracts]);

  // ── Derived KPIs ──────────────────────────────────────────────────────────
  const active = contracts.filter((c) => c.status === "Active");
  const totalMonthlyCost = active.reduce((s, c) => s + (c.totalPackage || 0), 0);
  const expiringSoon = active.filter((c) => c.daysRemaining >= 0 && c.daysRemaining <= 90).length;
  const deptCount = new Set(active.map((c) => c.department)).size;

  // ── Filtered list ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => contracts.filter((c) => {
    const matchSearch = c.staffName.toLowerCase().includes(search.toLowerCase())
      || c.staffRole.toLowerCase().includes(search.toLowerCase());
    const matchDept = deptFilter === "All" || c.department === deptFilter;
    const matchStatus = statusFilter === "All" || c.status === statusFilter;
    return matchSearch && matchDept && matchStatus;
  }), [contracts, search, deptFilter, statusFilter]);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const openAdd = () => { setEditId(null); setForm(EMPTY_FORM); setFormErrors({}); setDialogOpen(true); };
  const openEdit = (c) => {
    setEditId(c.id);
    setForm({
      staffName: c.staffName, staffRole: c.staffRole, department: c.department,
      contractType: c.contractType, monthlySalaryAmount: String(c.monthlySalary),
      currency: c.currency, housingAllowance: c.housingAllowance ? String(c.housingAllowance) : "",
      transportAllowance: c.transportAllowance ? String(c.transportAllowance) : "",
      performanceBonusAnnual: c.performanceBonus ? String(c.performanceBonus) : "",
      startDate: c.startDate, endDate: c.endDate,
      email: c.email || "", phoneNumber: c.phoneNumber || "", notes: c.notes || "",
    });
    setFormErrors({});
    setDialogOpen(true);
  };

  const fc = (field, val) => setForm((prev) => ({ ...prev, [field]: val }));

  const validate = () => {
    const e = {};
    if (!form.staffName.trim()) e.staffName = "Required";
    if (!form.staffRole.trim()) e.staffRole = "Required";
    if (!form.department) e.department = "Required";
    if (!form.monthlySalaryAmount || isNaN(Number(form.monthlySalaryAmount))) e.monthlySalaryAmount = "Valid amount required";
    if (!form.startDate) e.startDate = "Required";
    if (!form.endDate) e.endDate = "Required";
    return e;
  };

  const handleSave = async () => {
    const errors = validate();
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setLoading(true);
    try {
      const payload = {
        staffName: form.staffName, staffRole: form.staffRole,
        department: form.department, contractType: form.contractType,
        monthlySalaryAmount: Number(form.monthlySalaryAmount), currency: form.currency,
        housingAllowance: form.housingAllowance ? Number(form.housingAllowance) : null,
        transportAllowance: form.transportAllowance ? Number(form.transportAllowance) : null,
        performanceBonusAnnual: form.performanceBonusAnnual ? Number(form.performanceBonusAnnual) : null,
        startDate: form.startDate, endDate: form.endDate,
        email: form.email || null, phoneNumber: form.phoneNumber || null,
        notes: form.notes || null, tenantId: currentUser.tenantId,
      };
      if (editId) {
        await financeService.updateStaffContract(editId, payload);
      } else {
        await financeService.createStaffContract(payload);
      }
      await fetchContracts();
      setDialogOpen(false);
    } catch (err) {
      setFormErrors({ submit: err?.title ?? err?.message ?? "Save failed" });
    } finally {
      setLoading(false);
    }
  };

  const handleTerminate = async () => {
    if (!terminateId) return;
    setLoading(true);
    try {
      await financeService.terminateStaffContract(terminateId);
      await fetchContracts();
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setTerminateId(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        {/* Header */}
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftBox>
            <SoftTypography variant="h4" fontWeight="bold" color="dark">Staff Contracts</SoftTypography>
            <SoftTypography variant="body2" color="text">
              Non-player employment — coaches, medical, admin &amp; scouts · {CLUB_NAME}
            </SoftTypography>
          </SoftBox>
          <PermissionGate permission="finance.edit">
            <SoftButton variant="gradient" color="info" onClick={openAdd}>
              <Icon sx={{ mr: 0.5 }}>add</Icon>New Contract
            </SoftButton>
          </PermissionGate>
        </SoftBox>

        {/* Expiry Alert */}
        {expiringSoon > 0 && (
          <SoftBox mb={3}>
            <Alert severity="warning">
              <strong>{expiringSoon} staff contract{expiringSoon > 1 ? "s" : ""}</strong> expiring within 90 days — review and renew as needed.
            </Alert>
          </SoftBox>
        )}

        {/* KPIs */}
        <SoftBox mb={3}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Active Staff" }} count={active.length}
                percentage={{ color: "success", text: `across ${deptCount} departments` }}
                icon={{ color: "info", component: "badge" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Monthly Cost" }} count={formatCompact(totalMonthlyCost)}
                percentage={{ color: "warning", text: "inc. allowances" }}
                icon={{ color: "warning", component: "payments" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Annual Cost" }} count={formatCompact(totalMonthlyCost * 12)}
                percentage={{ color: "info", text: "projected full year" }}
                icon={{ color: "primary", component: "bar_chart" }} />
            </Grid>
            <Grid item xs={12} sm={6} xl={3}>
              <MiniStatisticsCard title={{ text: "Expiring ≤90 Days" }} count={expiringSoon}
                percentage={{ color: expiringSoon > 0 ? "error" : "success", text: expiringSoon > 0 ? "action needed" : "all clear" }}
                icon={{ color: expiringSoon > 0 ? "error" : "success", component: "event" }} />
            </Grid>
          </Grid>
        </SoftBox>

        {/* Table */}
        <Card>
          <SoftBox p={3}>
            <Grid container spacing={2} mb={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField fullWidth placeholder="Search name or role…" value={search}
                  onChange={(e) => setSearch(e.target.value)} size="small"
                  InputProps={{ startAdornment: <Icon sx={{ mr: 1, color: "#aaa" }}>search</Icon> }} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Department</InputLabel>
                  <Select value={deptFilter} label="Department" onChange={(e) => setDeptFilter(e.target.value)}>
                    <MenuItem value="All">All Departments</MenuItem>
                    {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
                    <MenuItem value="All">All</MenuItem>
                    <MenuItem value="Active">Active</MenuItem>
                    <MenuItem value="Expired">Expired</MenuItem>
                    <MenuItem value="Terminated">Terminated</MenuItem>
                    <MenuItem value="OnLeave">On Leave</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={2}>
                <SoftTypography variant="caption" color="text">{filtered.length} of {contracts.length}</SoftTypography>
              </Grid>
            </Grid>

            <SoftBox sx={{ overflowX: "auto" }}>
              <SoftBox component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <SoftBox component="thead" sx={{ backgroundColor: "#f8f9fa" }}>
                  <SoftBox component="tr">
                    {["Name", "Role / Dept", "Type", "Monthly Salary", "Total Package", "Contract Period", "Status", "Actions"].map((h) => (
                      <SoftBox key={h} component="th" sx={{
                        textAlign: "left", p: 1.5, color: "text.secondary",
                        fontWeight: 700, fontSize: "0.7rem", textTransform: "uppercase",
                        borderBottom: "2px solid #e9ecef", whiteSpace: "nowrap",
                      }}>{h}</SoftBox>
                    ))}
                  </SoftBox>
                </SoftBox>
                <SoftBox component="tbody">
                  {loading ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={8}><FootballLoader label="Loading staff contracts…" /></SoftBox>
                    </SoftBox>
                  ) : filtered.length === 0 ? (
                    <SoftBox component="tr">
                      <SoftBox component="td" colSpan={8} sx={{ textAlign: "center", p: 4 }}>
                        <SoftTypography variant="caption" color="text">No contracts match the current filters.</SoftTypography>
                      </SoftBox>
                    </SoftBox>
                  ) : filtered.map((c, i) => (
                    <SoftBox key={c.id} component="tr" sx={{
                      "&:hover": { backgroundColor: "#f8f9fa" },
                      backgroundColor: i % 2 === 0 ? "white" : "#fdfdfd",
                    }}>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="medium">{c.staffName}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="caption" color="text">{c.staffRole}</SoftTypography>
                        <br />
                        <SoftBadge badgeContent={c.department} color="secondary" size="xs" container />
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="caption" color="text">{c.contractType}</SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftTypography variant="button" fontWeight="medium">
                          {formatCompact(c.monthlySalary)}<SoftTypography variant="caption" color="text">/mo</SoftTypography>
                        </SoftTypography>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <Tooltip title={`Housing: ${c.housingAllowance ? formatCompact(c.housingAllowance) : "—"} · Transport: ${c.transportAllowance ? formatCompact(c.transportAllowance) : "—"}`}>
                          <SoftTypography variant="button" fontWeight="bold" color="dark">
                            {formatCompact(c.totalPackage)}/mo
                          </SoftTypography>
                        </Tooltip>
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0", whiteSpace: "nowrap" }}>
                        <SoftTypography variant="caption" color="text">
                          {formatDateIN(c.startDate)} → {formatDateIN(c.endDate)}
                        </SoftTypography>
                        {c.daysRemaining >= 0 && c.daysRemaining <= 90 && (
                          <Chip label={`${c.daysRemaining}d left`} size="small" color="warning" sx={{ ml: 1, fontSize: "0.65rem", height: 18 }} />
                        )}
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <SoftBadge badgeContent={c.status} color={STATUS_COLORS[c.status] || "info"} size="xs" container />
                      </SoftBox>
                      <SoftBox component="td" sx={{ p: 1.5, borderBottom: "1px solid #f0f0f0" }}>
                        <PermissionGate permission="finance.edit">
                          <SoftBox display="flex" gap={0.5}>
                            <Tooltip title="Edit">
                              <SoftButton variant="text" color="info" size="small" onClick={() => openEdit(c)} sx={{ minWidth: 0, p: 0.5 }}>
                                <Icon>edit</Icon>
                              </SoftButton>
                            </Tooltip>
                            {c.status === "Active" && (
                              <Tooltip title="Terminate">
                                <SoftButton variant="text" color="error" size="small" onClick={() => setTerminateId(c.id)} sx={{ minWidth: 0, p: 0.5 }}>
                                  <Icon>cancel</Icon>
                                </SoftButton>
                              </Tooltip>
                            )}
                          </SoftBox>
                        </PermissionGate>
                      </SoftBox>
                    </SoftBox>
                  ))}
                </SoftBox>
              </SoftBox>
            </SoftBox>
          </SoftBox>
        </Card>
      </SoftBox>
      <Footer company={{ href: "#", name: CLUB_NAME }} links={[]} />

      {/* ── Add / Edit Dialog ──────────────────────────────────────────────── */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>
          {editId ? "Edit Staff Contract" : "New Staff Contract"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} pt={1}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Full Name" required value={form.staffName}
                onChange={(e) => fc("staffName", e.target.value)}
                error={!!formErrors.staffName} helperText={formErrors.staffName} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Role / Designation" required value={form.staffRole}
                onChange={(e) => fc("staffRole", e.target.value)}
                error={!!formErrors.staffRole} helperText={formErrors.staffRole} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small" error={!!formErrors.department}>
                <InputLabel shrink>Department</InputLabel>
                <Select value={form.department} label="Department" onChange={(e) => fc("department", e.target.value)}>
                  {DEPARTMENTS.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Contract Type</InputLabel>
                <Select value={form.contractType} label="Contract Type" onChange={(e) => fc("contractType", e.target.value)}>
                  {CONTRACT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel shrink>Currency</InputLabel>
                <Select value={form.currency} label="Currency" onChange={(e) => fc("currency", e.target.value)}>
                  {["INR", "USD", "EUR", "GBP"].map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Monthly Salary" required type="number" value={form.monthlySalaryAmount}
                onChange={(e) => fc("monthlySalaryAmount", e.target.value)}
                error={!!formErrors.monthlySalaryAmount} helperText={formErrors.monthlySalaryAmount} size="small"
                inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Housing Allowance" type="number" value={form.housingAllowance}
                onChange={(e) => fc("housingAllowance", e.target.value)} size="small"
                inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Transport Allowance" type="number" value={form.transportAllowance}
                onChange={(e) => fc("transportAllowance", e.target.value)} size="small"
                inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} sm={3}>
              <TextField fullWidth label="Performance Bonus (Annual)" type="number" value={form.performanceBonusAnnual}
                onChange={(e) => fc("performanceBonusAnnual", e.target.value)} size="small"
                inputProps={{ min: 0 }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Start Date" required type="date" value={form.startDate}
                onChange={(e) => fc("startDate", e.target.value)}
                error={!!formErrors.startDate} helperText={formErrors.startDate} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="End Date" required type="date" value={form.endDate}
                onChange={(e) => fc("endDate", e.target.value)}
                error={!!formErrors.endDate} helperText={formErrors.endDate} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Email" type="email" value={form.email}
                onChange={(e) => fc("email", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Phone" value={form.phoneNumber}
                onChange={(e) => fc("phoneNumber", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Notes" multiline rows={2} value={form.notes}
                onChange={(e) => fc("notes", e.target.value)} size="small" InputLabelProps={{ shrink: true }} />
            </Grid>
            {formErrors.submit && (
              <Grid item xs={12}><Alert severity="error">{formErrors.submit}</Alert></Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setDialogOpen(false)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="info" onClick={handleSave}>
            {editId ? "Update" : "Create"} Contract
          </SoftButton>
        </DialogActions>
      </Dialog>

      {/* ── Terminate Confirm Dialog ───────────────────────────────────────── */}
      <Dialog open={!!terminateId} onClose={() => setTerminateId(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem" }}>Terminate Contract</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2" color="text">
            Are you sure you want to terminate this staff contract? This action cannot be undone.
          </SoftTypography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <SoftButton variant="outlined" color="secondary" onClick={() => setTerminateId(null)}>Cancel</SoftButton>
          <SoftButton variant="gradient" color="error" onClick={handleTerminate}>Terminate</SoftButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default StaffContractsPage;
